import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Modal, TextInput, Button, Group, Text, Stack } from '@mantine/core';
import { useIntlLight } from '../../../../libs/i18n';
import { requestEmailChange, validateEmailChange } from '../../../../services/request/email';

const emailSchema = yup.object({
  email: yup
    .string()
    .trim()
    .email('user.changeEmailEmailInvalid')
    .required('user.changeEmailEmailRequired'),
});

const codeSchema = yup.object({
  code: yup.string().trim().required('user.changeEmailCodeRequired'),
});

export default function ChangeEmailModal({
  opened,
  onClose,
}: {
  opened: boolean;
  onClose: () => void;
}) {
  const { t } = useIntlLight();
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const emailForm = useForm({ resolver: yupResolver(emailSchema) });
  const codeForm = useForm({ resolver: yupResolver(codeSchema) });

  const handleClose = () => {
    setStep('email');
    setSubmittedEmail('');
    setServerError(null);
    emailForm.reset();
    codeForm.reset();
    onClose();
  };

  const handleEmailSubmit = emailForm.handleSubmit(async data => {
    setLoading(true);
    setServerError(null);
    try {
      await requestEmailChange(data.email);
      setSubmittedEmail(data.email);
      setStep('code');
    } catch (error: any) {
      const message =
        error?.response?.data?.detail || error?.response?.data?.message || t('api.systemMessage');
      setServerError(message);
    } finally {
      setLoading(false);
    }
  });

  const handleCodeSubmit = codeForm.handleSubmit(async data => {
    setLoading(true);
    setServerError(null);
    try {
      await validateEmailChange(data.code);
      setShowSuccessModal(true);
      handleClose();
    } catch {
      setServerError(t('user.changeEmailInvalidCode'));
    } finally {
      setLoading(false);
    }
  });

  const handleSuccessConfirm = () => {
    setShowSuccessModal(false);
    window.location.href = '/logout';
  };

  return (
    <>
      <Modal
        opened={opened}
        onClose={handleClose}
        title={step === 'email' ? t('user.changeEmailTitle') : t('user.changeEmailVerifyTitle')}
      >
        {step === 'email' ? (
          <form onSubmit={handleEmailSubmit}>
            <Stack spacing="md">
              <TextInput
                label={t('user.changeEmailNewEmail')}
                {...emailForm.register('email')}
                error={
                  emailForm.formState.errors.email?.message
                    ? t(emailForm.formState.errors.email.message as string)
                    : serverError
                }
              />
              <Group position="right">
                <Button variant="subtle" onClick={handleClose}>
                  {t('app.cancel')}
                </Button>
                <Button type="submit" loading={loading}>
                  {t('user.changeEmailSend')}
                </Button>
              </Group>
            </Stack>
          </form>
        ) : (
          <form onSubmit={handleCodeSubmit}>
            <Stack spacing="md">
              <Text size="sm">{t('user.changeEmailCodeSent', { email: submittedEmail })}</Text>
              <TextInput
                label={t('user.changeEmailCode')}
                {...codeForm.register('code')}
                error={
                  codeForm.formState.errors.code?.message
                    ? t(codeForm.formState.errors.code.message as string)
                    : serverError
                }
              />
              <Group position="right">
                <Button variant="subtle" onClick={handleClose}>
                  {t('app.cancel')}
                </Button>
                <Button type="submit" loading={loading}>
                  {t('app.confirm')}
                </Button>
              </Group>
            </Stack>
          </form>
        )}
      </Modal>

      <Modal
        opened={showSuccessModal}
        onClose={handleSuccessConfirm}
        title={t('user.changeEmailSuccessTitle')}
        withCloseButton={false}
      >
        <Stack spacing="md">
          <Text>{t('user.changeEmailSuccessMessage')}</Text>
          <Text size="sm" c="dimmed">
            {t('user.changeEmailReloginRequired')}
          </Text>
          <Group position="right">
            <Button onClick={handleSuccessConfirm}>{t('app.ok')}</Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
